import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  UserPlusIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { registrationsAPI, playersAPI } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { getErrorMessage } from '../utils/errorMessage';
import { notifyRegistrationsChanged } from '../services/registrationEvents';
import type { Player } from '../types/player';
import PlayerPicker from '../components/players/PlayerPicker';
import type { RegistrationRequest, RegistrationStatus } from '../types/registration';

const FILTERS: { key: RegistrationStatus | 'all'; label: string }[] = [
  { key: 'pending', label: 'Függőben' },
  { key: 'approved', label: 'Jóváhagyva' },
  { key: 'rejected', label: 'Elutasítva' },
  { key: 'all', label: 'Összes' },
];

const STATUS_BADGES: Record<RegistrationStatus, { text: string; className: string }> = {
  pending: { text: 'Függőben', className: 'bg-amber-100 text-amber-800' },
  approved: { text: 'Jóváhagyva', className: 'bg-green-100 text-green-800' },
  rejected: { text: 'Elutasítva', className: 'bg-red-100 text-red-700' },
};

const formatDateTime = (value: string) => new Date(value).toLocaleString('hu-HU');

/**
 * Az önkiszolgáló jelentkezések elbírálása. A fiók a jóváhagyás pillanatában
 * jön létre - elutasításkor soha, ilyenkor a tárolt jelszó is törlődik.
 */
const Registrations: React.FC = () => {
  usePageTitle('Regisztrációk');
  const { showToast } = useToast();

  const [filter, setFilter] = useState<RegistrationStatus | 'all'>('pending');
  const [requests, setRequests] = useState<RegistrationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState<Player[]>([]);

  // Melyik jelentkezésnél van nyitva a jóváhagyó űrlap, és mi van beállítva
  const [openId, setOpenId] = useState<number | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCoach, setIsCoach] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const [playerId, setPlayerId] = useState<string>('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await registrationsAPI.getAll(filter === 'all' ? undefined : filter));
    } catch (err) {
      showToast(getErrorMessage(err, 'A jelentkezéseket nem sikerült betölteni.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // A játékos lista csak a jóváhagyó űrlaphoz kell, ezért külön töltjük
  useEffect(() => {
    if (openId === null || players.length > 0) return;

    playersAPI
      .getAll()
      .then(setPlayers)
      .catch(() => showToast('A játékos listát nem sikerült betölteni.', 'error'));
  }, [openId, players.length, showToast]);

  const openApproveForm = (request: RegistrationRequest) => {
    setOpenId(request.id);
    setIsAdmin(false);
    setIsCoach(false);
    setIsPlayer(!request.wantsParentAccount);
    setIsParent(request.wantsParentAccount);
    setPlayerId('');
  };

  const hasAnyRole = isAdmin || isCoach || isPlayer || isParent;

  const handleApprove = async (request: RegistrationRequest) => {
    if (!hasAnyRole) return;

    setBusyId(request.id);
    try {
      await registrationsAPI.approve(request.id, {
        isAdmin,
        isCoach,
        isPlayer,
        isParent,
        playerId: (isPlayer || isParent) && playerId ? Number(playerId) : undefined,
      });
      setOpenId(null);
      await load();
      notifyRegistrationsChanged();
      showToast(`${request.name} fiókja létrejött.`, 'success');
    } catch (err) {
      showToast(getErrorMessage(err, 'A jóváhagyás nem sikerült.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (request: RegistrationRequest) => {
    const reason = window.prompt(
      `Elutasítod ${request.name} (${request.email}) jelentkezését?\n\nIndoklás (a jelentkező emailben megkapja, elhagyható):`,
      ''
    );
    if (reason === null) return;

    setBusyId(request.id);
    try {
      await registrationsAPI.reject(request.id, reason.trim() || undefined);
      await load();
      notifyRegistrationsChanged();
      showToast('A jelentkezést elutasítottuk, fiók nem jött létre.', 'info');
    } catch (err) {
      showToast(getErrorMessage(err, 'Az elutasítás nem sikerült.'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Regisztrációk</h1>
        <p className="mt-1 text-sm text-gray-500">
          A bejelentkezési oldalon beküldött jelentkezések. Fiók csak jóváhagyáskor jön létre - a
          jelentkező addig nem tud belépni.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`rounded-lg border px-4 py-2 text-sm font-medium ${
              filter === f.key
                ? 'border-primary-600 bg-primary-600 text-white'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-24 animate-pulse rounded-lg bg-gray-100" />
      ) : requests.length === 0 ? (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
          {filter === 'pending'
            ? 'Nincs elbírálásra váró jelentkezés.'
            : 'Nincs megjeleníthető jelentkezés.'}
        </div>
      ) : (
        <ul className="space-y-3">
          {requests.map((request) => {
            const badge = STATUS_BADGES[request.status];
            const isOpen = openId === request.id;
            const busy = busyId === request.id;

            return (
              <li key={request.id} className="rounded-lg bg-white p-4 shadow md:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{request.name}</span>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                      >
                        {badge.text}
                      </span>
                      {request.wantsParentAccount && request.status === 'pending' && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                          szülőként jelentkezett
                        </span>
                      )}
                    </div>
                    <p className="mt-1 break-all text-sm text-gray-600">{request.email}</p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                      <ClockIcon className="h-4 w-4" />
                      {formatDateTime(request.createdAt)}
                    </p>
                    {request.note && (
                      <p className="mt-2 rounded-md bg-gray-50 p-2 text-sm text-gray-700">
                        {request.note}
                      </p>
                    )}
                    {request.status !== 'pending' && (
                      <p className="mt-2 text-xs text-gray-500">
                        {request.decidedAt && `Elbírálva: ${formatDateTime(request.decidedAt)}`}
                        {request.decidedByUserName && ` · ${request.decidedByUserName}`}
                        {request.rejectionReason && ` · Indoklás: ${request.rejectionReason}`}
                      </p>
                    )}
                  </div>

                  {request.status === 'pending' && !isOpen && (
                    <div className="flex shrink-0 gap-2">
                      <button
                        type="button"
                        onClick={() => openApproveForm(request)}
                        className="inline-flex items-center gap-1 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        Jóváhagyás
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReject(request)}
                        disabled={busy}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        <XCircleIcon className="h-4 w-4" />
                        Elutasítás
                      </button>
                    </div>
                  )}
                </div>

                {isOpen && (
                  <div className="mt-4 space-y-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        Szerepkörök * <span className="font-normal text-gray-400">(egyszerre több is választható)</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isAdmin}
                            onChange={(e) => setIsAdmin(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Admin</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isCoach}
                            onChange={(e) => setIsCoach(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Edző</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isPlayer}
                            onChange={(e) => setIsPlayer(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Játékos</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={isParent}
                            onChange={(e) => setIsParent(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">Szülő</span>
                        </label>
                      </div>
                      {!hasAnyRole && (
                        <p className="mt-1 text-xs text-amber-600">Válassz legalább egy szerepkört.</p>
                      )}
                    </div>

                    {(isPlayer || isParent) && (
                      <div>
                        <label
                          htmlFor={`player-${request.id}`}
                          className="mb-1 block text-sm font-medium text-gray-700"
                        >
                          Játékoshoz kapcsolás
                        </label>
                        <PlayerPicker
                          id={`player-${request.id}`}
                          mode="single"
                          players={players}
                          selectedIds={playerId ? [Number(playerId)] : []}
                          onChange={(ids) => setPlayerId(ids[0] ? String(ids[0]) : '')}
                        />
                      </div>
                    )}

                    <p className="text-xs text-gray-500">
                      A szerepkört te adod meg - a jelentkező jelzése csak tájékoztatás. A
                      jóváhagyás után azzal a jelszóval tud belépni, amit a regisztrációkor
                      megadott.
                    </p>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleApprove(request)}
                        disabled={busy || !hasAnyRole}
                        className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        <UserPlusIcon className="h-4 w-4" />
                        {busy ? 'Létrehozás...' : 'Fiók létrehozása'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setOpenId(null)}
                        className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Mégse
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Registrations;
